import React, { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import ConfigPageLayout from '../components/ConfigPageLayout'

export default function SendGridConfigPage() {
  const [form, setForm] = useState({ fromName: '', fromEmail: '', apiKey: '' })
  const [hasStoredKey, setHasStoredKey] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [testEmail, setTestEmail] = useState('')
  const [testSending, setTestSending] = useState(false)
  const [testStatus, setTestStatus] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/global-config')
      setForm({
        fromName: data.sendgrid_from_name || '',
        fromEmail: data.sendgrid_from_email || '',
        apiKey: '',
      })
      setHasStoredKey(data.has_sendgrid_api_key || false)
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setSaving(true)
      setStatus(null)
      setError(null)

      try {
        await api.put('/global-config', {
          sendgrid_from_name: form.fromName || null,
          sendgrid_from_email: form.fromEmail || null,
          sendgrid_api_key: form.apiKey || null,
        })
        setStatus('Configuration SendGrid mise à jour ✅')
        setHasStoredKey(!!form.apiKey)
        setForm((prev) => ({ ...prev, apiKey: '' }))
      } catch (err: any) {
        const detail = err?.response?.data?.detail
        setError(typeof detail === 'string' ? detail : err?.message || 'Erreur lors de la sauvegarde')
      } finally {
        setSaving(false)
      }
    },
    [form]
  )

  const handleSendTest = useCallback(async () => {
    if (!testEmail) return
    setTestSending(true)
    setTestStatus(null)
    setTestError(null)

    try {
      await api.post('/global-config/sendgrid/test', { email: testEmail })
      setTestStatus('E-mail de test envoyé ✅')
      setTestEmail('')
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setTestError(typeof detail === 'string' ? detail : err?.message || "Erreur lors de l'envoi")
    } finally {
      setTestSending(false)
    }
  }, [testEmail])

  if (loading) {
    return (
      <ConfigPageLayout title="SENDGRID" description="Configuration des e-mails transactionnels">
        <div className="flex items-center justify-center py-12">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </ConfigPageLayout>
    )
  }

  return (
    <ConfigPageLayout title="SENDGRID" description="Configuration des e-mails transactionnels">
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-6 rounded-3xl border border-emerald-100/80 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Configuration SendGrid</h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">Nom de l'expéditeur</label>
              <input
                type="text"
                value={form.fromName}
                onChange={(e) => setForm((prev) => ({ ...prev, fromName: e.target.value }))}
                className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100"
                placeholder="Support Totemium"
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">Adresse e-mail</label>
              <input
                type="email"
                value={form.fromEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, fromEmail: e.target.value }))}
                className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100"
                placeholder="support@votre-domaine.com"
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">Clé API SendGrid</label>
              <input
                type="password"
                value={form.apiKey}
                onChange={(e) => setForm((prev) => ({ ...prev, apiKey: e.target.value }))}
                className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100"
                placeholder={hasStoredKey ? 'Laissez vide pour conserver la clé actuelle' : 'SG.xxxxx'}
                disabled={saving}
              />
              <p className="text-xs text-zinc-400 dark:text-zinc-500">
                {hasStoredKey ? 'Une clé est déjà enregistrée' : 'Renseignez une clé SendGrid valide'}
              </p>
            </div>

            {status && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                {status}
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={saving}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer la configuration'}
            </button>
          </form>
        </section>

        <section className="space-y-6 rounded-3xl border border-zinc-200/70 bg-white/95 p-8 shadow-lg dark:border-zinc-800 dark:bg-zinc-900/90">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Test d'envoi</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Envoyez un e-mail de test pour vérifier votre configuration SendGrid
          </p>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-300">Adresse e-mail de test</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="w-full rounded-xl border border-zinc-200/70 bg-white/80 px-4 py-2.5 text-sm text-zinc-700 shadow-sm transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-zinc-700 dark:bg-zinc-950/70 dark:text-zinc-100"
              placeholder="test@example.com"
              disabled={testSending}
            />
          </div>

          {testStatus && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
              {testStatus}
            </div>
          )}
          {testError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              {testError}
            </div>
          )}

          <button
            type="button"
            onClick={handleSendTest}
            className="rounded-full border border-emerald-200/70 bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={testSending || !testEmail}
          >
            {testSending ? 'Envoi…' : 'Envoyer un e-mail de test'}
          </button>
        </section>
      </div>
    </ConfigPageLayout>
  )
}
